'use client'

import * as React from 'react'
import { Plus } from 'lucide-react'
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { Textarea } from 'components/ui/textarea'
import { toast } from 'sonner'
import { cn } from 'lib/utils'
import { auth, db } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

const AUDIT_COLLECTION = 'auditLogs'

const severityOptions = [
  { value: 'low', label: 'Low', badge: 'bg-success/15 text-success' },
  { value: 'medium', label: 'Medium', badge: 'bg-warning/15 text-warning' },
  { value: 'high', label: 'High', badge: 'bg-destructive/15 text-destructive' },
  { value: 'critical', label: 'Critical', badge: 'bg-destructive text-primary-foreground' },
]

const statusOptions = [
  { value: 'open', label: 'Open', badge: 'bg-destructive/15 text-destructive' },
  { value: 'in-progress', label: 'In Progress', badge: 'bg-primary/15 text-primary' },
  { value: 'resolved', label: 'Resolved', badge: 'bg-success/15 text-success' },
  { value: 'closed', label: 'Closed', badge: 'bg-muted text-muted-foreground' },
]

const categoryOptions = ['Operational', 'Financial', 'Technical', 'Procurement', 'Stakeholder', 'Compliance', 'Other']

function formatDate(value) {
  if (!value) return '—'
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ProjectIssuesPage() {
  const { project } = useProject()
  const { canEdit } = useUser()
  const [issues, setIssues] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [open, setOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [form, setForm] = React.useState({
    title: '',
    description: '',
    category: 'Operational',
    severity: 'medium',
    status: 'open',
    assigneeName: '',
  })

  React.useEffect(() => {
    let isMounted = true

    async function loadIssues() {
      try {
        const issuesQuery = query(collection(db, 'issues'), where('projectId', '==', project.id))
        const snapshot = await getDocs(issuesQuery)
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        setIssues(records)
      } catch {
        setIssues([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadIssues()

    return () => {
      isMounted = false
    }
  }, [project.id])

  const handleSaveIssue = async () => {
    if (!form.title.trim()) {
      toast.error('Please enter an issue title.')
      return
    }
    if (saving) return
    setSaving(true)
    try {
      const payload = {
        projectId: project.id,
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category || 'Operational',
        severity: form.severity || 'medium',
        status: form.status || 'open',
        assigneeName: form.assigneeName.trim() || 'Unassigned',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      const docRef = await addDoc(collection(db, 'issues'), payload)
      setIssues((prev) => [{ id: docRef.id, ...payload }, ...prev])
      try {
        const currentUser = auth.currentUser
        await addDoc(collection(db, AUDIT_COLLECTION), {
          timestamp: serverTimestamp(),
          userId: currentUser?.uid || 'system',
          userDisplayName: currentUser?.displayName || currentUser?.email || 'System',
          action: 'CREATE',
          entity: 'Project',
          entityId: project.id,
          changes: {
            before: '',
            after: `Added project issue "${payload.title}"`,
          },
        })
      } catch (error) {
        console.warn('Failed to write audit log', error)
      }
      toast.success('Issue added.')
      setForm({
        title: '',
        description: '',
        category: 'Operational',
        severity: 'medium',
        status: 'open',
        assigneeName: '',
      })
      setOpen(false)
    } catch (error) {
      toast.error('Failed to add issue', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Issues Register</CardTitle>
          {canEdit ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="size-4 mr-1" />
                  Add Issue
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Project Issue</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={form.title}
                    onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                    placeholder="Brief issue title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                    placeholder="Describe the issue and impact"
                    rows={3}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={form.category}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Severity</Label>
                    <Select
                      value={form.severity}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, severity: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select severity" />
                      </SelectTrigger>
                      <SelectContent>
                        {severityOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={form.status}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assignee</Label>
                    <Input
                      value={form.assigneeName}
                      onChange={(event) => setForm((prev) => ({ ...prev, assigneeName: event.target.value }))}
                      placeholder="Issue owner"
                    />
                  </div>
                </div>
                <Button className="w-full" onClick={handleSaveIssue} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Issue'}
                </Button>
              </div>
              </DialogContent>
            </Dialog>
          ) : null}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Issue</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Logged</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      Loading issues...
                    </TableCell>
                  </TableRow>
                ) : issues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      No issues logged yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  issues.map((issue) => {
                    const severity = severityOptions.find((option) => option.value === issue.severity)
                    const status = statusOptions.find((option) => option.value === issue.status)
                    return (
                      <TableRow key={issue.id}>
                        <TableCell className="min-w-[220px]">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-foreground">{issue.title || 'Issue'}</span>
                            <span className="text-xs text-muted-foreground line-clamp-1">
                              {issue.description || '—'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {issue.category || 'Operational'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn('text-[10px] capitalize', severity?.badge)}>
                            {issue.severity || 'medium'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {issue.assigneeName || 'Unassigned'}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn('text-[10px] capitalize', status?.badge)}>
                            {status?.label || issue.status || 'open'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDate(issue.createdAt)}
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
